import React, { useState, useEffect } from 'react';
import { X, Package, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { itemsAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';

const AdjustStockModal = ({ isOpen, item, onClose, onSuccess }) => {
  const [adjustmentType, setAdjustmentType] = useState('IN');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentStock, setCurrentStock] = useState(0);

  useEffect(() => {
    if (item) {
      setCurrentStock(item.current_stock || 0);
      // Reset form
      setAdjustmentType('IN');
      setQuantity('');
      setReason('');
    }
  }, [item]);

  if (!isOpen || !item) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity greater than 0');
      return;
    }
    
    if (!reason.trim()) {
      toast.error('Please provide a reason for the adjustment');
      return;
    }
    
    // Check for negative stock
    if (adjustmentType === 'OUT' && qty > currentStock) {
      toast.error(`Cannot reduce stock by ${qty}. Current stock is only ${currentStock}`);
      return;
    }

    setLoading(true);
    try {
      const response = await itemsAPI.adjustStock(item.id, {
        adjustment_type: adjustmentType,
        quantity: qty,
        reason: reason.trim()
      });
      
      const newStock = response.data?.new_stock ?? response.new_stock;
      toast.success(
        `Stock ${adjustmentType === 'IN' ? 'increased' : 'decreased'} by ${qty}. New stock: ${newStock}`
      );
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Adjust stock error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to adjust stock';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const newStockPreview = adjustmentType === 'IN' 
    ? currentStock + (parseFloat(quantity) || 0)
    : currentStock - (parseFloat(quantity) || 0);

  const commonReasons = adjustmentType === 'IN' 
    ? ['Stock received', 'Return from customer', 'Inventory correction', 'Transfer in']
    : ['Damaged/spoiled', 'Return to supplier', 'Lost/theft', 'Inventory correction', 'Sample/testing'];

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="adjust-stock-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-teal-50 to-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800">Adjust Stock</h2>
              <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[200px]">{item.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Current Stock Info */}
          <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Current Stock</p>
              <p className="text-xl font-bold text-slate-700 mt-0.5">
                {currentStock} <span className="text-xs font-normal text-slate-400">{item.unit || item.primary_unit || 'PCS'}</span>
              </p>
            </div>
            {currentStock === 0 && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-red-100 text-red-700 rounded-md">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span className="text-[10px] font-medium">Out of Stock</span>
              </div>
            )}
          </div>

          {/* Adjustment Type */}
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Adjustment Type *</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAdjustmentType('IN')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  adjustmentType === 'IN'
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="adjustment-type-in"
              >
                <TrendingUp className="w-4 h-4" />
                Stock In
              </button>
              <button
                type="button"
                onClick={() => setAdjustmentType('OUT')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  adjustmentType === 'OUT'
                    ? 'bg-red-500 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="adjustment-type-out"
              >
                <TrendingDown className="w-4 h-4" />
                Stock Out
              </button>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <Label htmlFor="quantity" className="text-xs text-slate-600 mb-2 block">Quantity *</Label>
            <Input
              id="quantity"
              type="number"
              min="0.01"
              step="0.01"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity to adjust"
              className="text-base"
              data-testid="adjustment-quantity"
              autoFocus
            />
          </div>

          {/* Reason */}
          <div>
            <Label htmlFor="reason" className="text-xs text-slate-600 mb-2 block">Reason *</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for adjustment"
              rows={2}
              className="resize-none"
              data-testid="adjustment-reason"
            />
            {/* Quick reason chips */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {commonReasons.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  className="px-2 py-0.5 text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-600 rounded transition-colors"
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          {quantity && parseFloat(quantity) > 0 && (
            <div className={`rounded-lg p-3 ${
              adjustmentType === 'IN' ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-600">New Stock After Adjustment</span>
                <span className={`text-lg font-bold ${
                  adjustmentType === 'IN' ? 'text-emerald-600' : newStockPreview < 0 ? 'text-red-600' : 'text-red-600'
                }`}>
                  {newStockPreview.toFixed(2)} {item.unit || item.primary_unit || 'PCS'}
                </span>
              </div>
              {newStockPreview < 0 && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Warning: This will result in negative stock!
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !quantity || parseFloat(quantity) <= 0 || !reason.trim()}
              className={`flex-1 ${
                adjustmentType === 'IN' 
                  ? 'bg-emerald-500 hover:bg-emerald-600' 
                  : 'bg-red-500 hover:bg-red-600'
              }`}
              data-testid="adjust-stock-submit"
            >
              {loading ? 'Processing...' : `${adjustmentType === 'IN' ? 'Add' : 'Remove'} Stock`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdjustStockModal;
